import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import EmptyState from '../components/EmptyState';
import { useClientsData, useCreateQuote, useQuotesData, useSettingsData } from '../hooks/useData';
import QuoteActions from '../components/QuoteActions';
import QuoteDetailDrawer from '../components/QuoteDetailDrawer';
import { sitesApi } from '../utils/api';
import {
  ChevronLeftIcon,
  ChevronRightIcon,
  DocumentTextIcon,
  PlusIcon,
  TrashIcon,
  UserIcon,
  CalendarDaysIcon,
  ArrowRightIcon,
} from '@heroicons/react/24/outline';
import { Search as SearchIcon } from 'lucide-react';

const formatDate = (dateString: string) => {
  if (!dateString) return 'N/A';
  const date = new Date(dateString);
  return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });
};

type QuoteComposerItem = {
  id: number;
  description: string;
  quantity: string;
  unitPrice: string;
  itemType: 'SERVICE' | 'MATERIAL' | 'LABOUR' | 'CALLOUT';
};

type QuoteComposerProps = {
  onCancel: () => void;
};

const formatMoney = (value: number) => {
  if (!Number.isFinite(value)) return '£0.00';
  return `£${value.toFixed(2)}`;
};

function QuoteComposer({ onCancel }: QuoteComposerProps) {
  const { data: clients = [] } = useClientsData();
  const { data: settingsData } = useSettingsData();
  const createQuoteMutation = useCreateQuote();
  const company = (settingsData as any)?.company || {};
  const companyLogoUrl = company?.logo as string | undefined;

  const [showClientDropdown, setShowClientDropdown] = useState(false);
  const [clientName, setClientName] = useState('');
  const [clientAddress, setClientAddress] = useState('');
  const [selectedClientId, setSelectedClientId] = useState('');

  const [jobTitle, setJobTitle] = useState('');
  const [description, setDescription] = useState('');
  const [validUntil, setValidUntil] = useState('');
  const [notes, setNotes] = useState('');
  const [terms, setTerms] = useState('');
  const [siteAddress, setSiteAddress] = useState('');
  const [sitePostcode, setSitePostcode] = useState('');
  const [clientSites, setClientSites] = useState<any[]>([]);
  const [showSiteDropdown, setShowSiteDropdown] = useState(false);

  // Load the client's existing sites whenever selection changes
  useEffect(() => {
    let cancelled = false;
    if (!selectedClientId) {
      setClientSites([]);
      return;
    }
    (async () => {
      try {
        const res = await sitesApi.getAll(selectedClientId);
        if (!cancelled) setClientSites((res as any).data || []);
      } catch (err) {
        console.error('Failed to load sites for client', err);
        if (!cancelled) setClientSites([]);
      }
    })();
    return () => { cancelled = true; };
  }, [selectedClientId]);

  const siteSuggestions = (() => {
    const q = siteAddress.trim().toLowerCase();
    if (!clientSites.length) return [];
    if (!q) return clientSites.slice(0, 6);
    return clientSites
      .filter((s) => {
        const hay = `${s.name || ''} ${s.address || ''} ${s.postcode || ''}`.toLowerCase();
        return hay.includes(q);
      })
      .slice(0, 6);
  })();
  const [requiredSkills, setRequiredSkills] = useState<string[]>([]);
  const [skillInput, setSkillInput] = useState('');
  const addSkill = (raw: string) => {
    const s = String(raw || '').trim();
    if (!s) return;
    setRequiredSkills((prev) => (prev.map((x) => x.toLowerCase()).includes(s.toLowerCase()) ? prev : [...prev, s]));
    setSkillInput('');
  };
  const removeSkill = (skill: string) =>
    setRequiredSkills((prev) => prev.filter((s) => s !== skill));

  const [items, setItems] = useState<QuoteComposerItem[]>([
    { id: 1, description: '', quantity: '1', unitPrice: '0', itemType: 'SERVICE' },
  ]);

  const vatPercent = 20;

  const subtotal = items.reduce((sum, item) => {
    const qty = parseFloat(item.quantity || '0') || 0;
    const unit = parseFloat(item.unitPrice || '0') || 0;
    return sum + qty * unit;
  }, 0);

  const vatAmount = subtotal * (vatPercent / 100);
  const total = subtotal + vatAmount;

  const handleItemChange = (id: number, field: keyof QuoteComposerItem, value: string) => {
    setItems((prev) => prev.map((item) => (item.id === id ? { ...item, [field]: value } : item)));
  };

  const handleAddItem = () => {
    setItems((prev) => {
      const nextId = prev.length > 0 ? Math.max(...prev.map((i) => i.id)) + 1 : 1;
      return [
        ...prev,
        { id: nextId, description: '', quantity: '1', unitPrice: '0', itemType: 'SERVICE' },
      ];
    });
  };

  const handleRemoveItem = (id: number) => {
    setItems((prev) => (prev.length > 1 ? prev.filter((item) => item.id !== id) : prev));
  };

  const handleSaveDraft = () => {
    const payload = {
      clientName,
      clientAddress,
      selectedClientId,
      jobTitle,
      description,
      validUntil,
      notes,
      terms,
      items,
      totals: { subtotal, vatAmount, total },
    };
    console.log('Saving quote draft:', payload);
  };

  const handleSubmit = async () => {
    let clientId = selectedClientId;
    if (!clientId && clientName.trim()) {
      const match = (clients as any[]).find(
        (c: any) => (c.name || '').toLowerCase().trim() === clientName.toLowerCase().trim()
      );
      if (match?.id) {
        clientId = match.id;
        setSelectedClientId(match.id);
      }
    }

    if (!clientId) {
      alert('Please select a client');
      return;
    }
    if (!jobTitle.trim()) {
      alert('Please enter a job title');
      return;
    }
    if (!validUntil) {
      alert('Please choose a valid until date');
      return;
    }

    const lineItems = items
      .map((item, index) => {
        const qty = parseFloat(item.quantity || '0') || 0;
        const unit = parseFloat(item.unitPrice || '0') || 0;
        return {
          description: item.description,
          quantity: qty,
          unitPrice: unit,
          totalPrice: qty * unit,
          itemType: item.itemType,
          sortOrder: index + 1,
        };
      })
      .filter((item) => item.description.trim() !== '');

    if (lineItems.length === 0) {
      alert('Please add at least one quote item');
      return;
    }

    try {
      console.log('Creating quote with data:', {
        clientId,
        jobTitle,
        description,
        validUntil,
        notes,
        terms,
        items: lineItems,
      });
      await createQuoteMutation.mutateAsync({
        clientId,
        jobTitle,
        description,
        validUntil,
        notes,
        terms,
        siteAddress: siteAddress || undefined,
        sitePostcode: sitePostcode || undefined,
        requiredSkills,
        items: lineItems,
      });
      onCancel();
    } catch (err: any) {
      console.error('Error creating quote:', err);
      console.error('Error response:', err?.response?.data);
      console.error('Error status:', err?.response?.status);
      const errorMessage = err?.response?.data?.error || err?.response?.data?.message || err?.message || 'Unknown error';
      const errorDetails = err?.response?.data?.details ? JSON.stringify(err?.response?.data?.details) : '';
      alert(`Failed to create quote: ${errorMessage}${errorDetails ? `\nDetails: ${errorDetails}` : ''}`);
    }
  };

  return (
    <div className="py-6">
      <div className="mb-4 border-b border-[#E6E6E4] pb-3">
        <div className="flex items-center gap-2 text-xs text-[#515959] mb-4">
          <button
            type="button"
            className="inline-flex h-7 w-7 items-center justify-center rounded-md border border-[#D4D4D4] bg-white text-[#4B5563] hover:bg-[#F3F4F6]"
            onClick={onCancel}
          >
            <ChevronLeftIcon className="h-3 w-3" />
          </button>
          <span className="text-xs font-medium text-orange-600 uppercase tracking-[0.12em]">Draft</span>
        </div>

        <div className="flex items-start justify-between">
          <div>
            <h1 className="text-2xl font-semibold text-[#191919] leading-tight">Create quote</h1>
            <p className="mt-1 text-xs font-normal text-[#515959]">
              Build a new quote, review a live preview, and get it ready to send to your client.
            </p>
          </div>

          <div className="flex flex-wrap items-center gap-3">
            <button
              type="button"
              onClick={handleSaveDraft}
              className="inline-flex items-center justify-center rounded-lg border border-[#CDCDCD] bg-white px-3 py-2 text-xs font-medium text-[#383838] hover:bg-[#F3F2F0] transition-colors"
            >
              Save draft
            </button>

            <button
              type="button"
              onClick={handleSubmit}
              disabled={createQuoteMutation.isPending}
              className="inline-flex items-center justify-center rounded-lg bg-orange-500 px-4 py-2 text-xs font-medium text-white shadow-sm hover:bg-orange-600 transition-colors disabled:opacity-50"
            >
              {createQuoteMutation.isPending ? 'Creating…' : 'Create quote'}
            </button>
          </div>
        </div>
      </div>

      <div className="grid gap-6 lg:grid-cols-[minmax(0,1.4fr)_minmax(0,1fr)]">
        <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
          <div className="flex items-center justify-between">
            <h2 className="text-sm font-medium text-gray-900 tracking-[-0.03em]">Details</h2>
            <span className="text-xs text-gray-400">DRAFT</span>
          </div>
          <p className="mt-1 text-xs text-gray-500">Set who you are quoting and what you are quoting them for.</p>

          <div className="mt-4 space-y-4">
            <div className="space-y-1">
              <label className="text-xs font-medium text-gray-700">Client</label>
              <div className="relative">
                <input
                  type="text"
                  value={clientName}
                  onChange={(e) => {
                    const value = e.target.value;
                    setClientName(value);
                    setSelectedClientId('');
                    setShowClientDropdown(true);
                  }}
                  onFocus={() => setShowClientDropdown(true)}
                  placeholder="Search or enter client name"
                  className="h-9 w-full rounded-lg border border-gray-300 bg-white px-3 text-sm text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                />
                {showClientDropdown && clientName && (
                  <div className="absolute z-10 mt-1 w-full overflow-hidden rounded-lg border border-gray-200 bg-white shadow-lg">
                    {((clients as any[]) || [])
                      .filter((client: any) =>
                        (client.name || '').toLowerCase().includes(clientName.toLowerCase())
                      )
                      .slice(0, 6)
                      .map((client: any) => (
                        <button
                          key={client.id}
                          type="button"
                          className="flex w-full items-start justify-between px-3 py-2 text-left text-xs hover:bg-gray-50"
                          onClick={() => {
                            setSelectedClientId(client.id);
                            setClientName(client.name || '');
                            setShowClientDropdown(false);
                            if (client.address) {
                              setClientAddress(client.address);
                            }
                          }}
                        >
                          <span className="font-medium text-gray-900">{client.name}</span>
                          {client.address && (
                            <span className="ml-2 truncate text-[11px] text-gray-500 max-w-[160px]">
                              {client.address}
                            </span>
                          )}
                        </button>
                      ))}
                  </div>
                )}
              </div>
            </div>

            <div className="space-y-1">
              <label className="text-xs font-medium text-gray-700">Address</label>
              <textarea
                rows={2}
                value={clientAddress}
                onChange={(e) => setClientAddress(e.target.value)}
                placeholder="Customer address"
                className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
              />
            </div>

            <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
              <div className="space-y-1">
                <label className="text-xs font-medium text-gray-700">Job title</label>
                <input
                  type="text"
                  value={jobTitle}
                  onChange={(e) => setJobTitle(e.target.value)}
                  placeholder="e.g. Boiler installation"
                  className="h-9 w-full rounded-lg border border-gray-300 bg-white px-3 text-sm text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                />
              </div>
              <div className="space-y-1">
                <label className="text-xs font-medium text-gray-700">Valid until</label>
                <input
                  type="date"
                  value={validUntil}
                  onChange={(e) => setValidUntil(e.target.value)}
                  min={new Date().toISOString().split('T')[0]}
                  className="h-9 w-full rounded-lg border border-gray-300 bg-white px-3 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                />
              </div>
            </div>

            <div className="space-y-1">
              <label className="text-xs font-medium text-gray-700">Description</label>
              <textarea
                rows={3}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Describe the work to be quoted for"
                className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
              />
            </div>

            <div className="grid grid-cols-1 gap-3 md:grid-cols-[minmax(0,2fr)_minmax(0,1fr)]">
              <div className="space-y-1">
                <label className="text-xs font-medium text-gray-700">
                  Site <span className="text-[10px] text-gray-400 font-normal">
                    {selectedClientId
                      ? clientSites.length
                        ? `(${clientSites.length} saved for this client)`
                        : '(no saved sites — type a new one)'
                      : '(pick a client to see saved sites)'}
                  </span>
                </label>
                <div className="relative">
                  <input
                    type="text"
                    value={siteAddress}
                    onChange={(e) => {
                      setSiteAddress(e.target.value);
                      setShowSiteDropdown(true);
                    }}
                    onFocus={() => setShowSiteDropdown(true)}
                    onBlur={() => setTimeout(() => setShowSiteDropdown(false), 150)}
                    placeholder={selectedClientId ? 'Type site name or address…' : 'Where will the work be carried out?'}
                    className="h-9 w-full rounded-lg border border-gray-300 bg-white px-3 text-sm text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                  />
                  {showSiteDropdown && siteSuggestions.length > 0 && (
                    <div className="absolute z-10 mt-1 w-full max-h-60 overflow-auto rounded-lg border border-gray-200 bg-white shadow-lg">
                      {siteSuggestions.map((s: any) => (
                        <button
                          key={s.id}
                          type="button"
                          className="flex w-full flex-col items-start gap-0.5 px-3 py-2 text-left text-xs hover:bg-gray-50"
                          onMouseDown={(e) => {
                            // onMouseDown fires before blur, so selection registers
                            e.preventDefault();
                            const composed = [s.name, s.address].filter(Boolean).join(' — ');
                            setSiteAddress(composed || s.name || s.address || '');
                            if (s.postcode) setSitePostcode(String(s.postcode).toUpperCase());
                            setShowSiteDropdown(false);
                          }}
                        >
                          <span className="font-medium text-gray-900">{s.name || s.address || 'Unnamed site'}</span>
                          {(s.address || s.postcode) && (
                            <span className="text-[11px] text-gray-500 truncate w-full">
                              {s.address}
                              {s.postcode ? ` · ${s.postcode}` : ''}
                            </span>
                          )}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
              <div className="space-y-1">
                <label className="text-xs font-medium text-gray-700">Site postcode</label>
                <input
                  type="text"
                  value={sitePostcode}
                  onChange={(e) => setSitePostcode(e.target.value.toUpperCase())}
                  placeholder="SW1A 1AA"
                  className="h-9 w-full rounded-lg border border-gray-300 bg-white px-3 text-sm text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent uppercase"
                />
              </div>
            </div>

            <div className="space-y-1">
              <label className="text-xs font-medium text-gray-700">
                Required skills <span className="text-[10px] text-gray-400 font-normal">(helps auto-assign the right engineer)</span>
              </label>
              <div className="flex flex-wrap gap-1.5 min-h-[36px] px-2 py-1.5 rounded-lg border border-gray-300 bg-white focus-within:ring-2 focus-within:ring-orange-500 focus-within:border-transparent">
                {requiredSkills.map((skill) => (
                  <span key={skill} className="inline-flex items-center gap-1 bg-orange-50 text-orange-700 px-2 py-0.5 rounded-full text-xs font-medium">
                    {skill}
                    <button type="button" onClick={() => removeSkill(skill)} className="text-orange-400 hover:text-orange-700" aria-label={`Remove ${skill}`}>
                      ×
                    </button>
                  </span>
                ))}
                <input
                  type="text"
                  value={skillInput}
                  onChange={(e) => setSkillInput(e.target.value)}
                  onKeyDown={(e) => {
                    if ((e.key === 'Enter' || e.key === ',') && skillInput.trim()) {
                      e.preventDefault();
                      addSkill(skillInput);
                    } else if (e.key === 'Backspace' && !skillInput && requiredSkills.length > 0) {
                      removeSkill(requiredSkills[requiredSkills.length - 1]);
                    }
                  }}
                  onBlur={() => skillInput.trim() && addSkill(skillInput)}
                  className="flex-1 min-w-[140px] text-sm outline-none bg-transparent"
                  placeholder={requiredSkills.length === 0 ? 'e.g. Gas Safe, Boiler install (press Enter)' : 'Add another…'}
                />
              </div>
            </div>

            <div className="space-y-3">
              {items.map((item, index) => (
                <div
                  key={item.id}
                  className="grid grid-cols-[minmax(0,2fr)_minmax(0,0.7fr)_minmax(0,0.9fr)_minmax(0,0.9fr)_auto] gap-2 items-center"
                >
                  <div className="space-y-1">
                    <label className="text-xs font-medium text-gray-700">
                      {index === 0 ? 'Item' : 'Additional item'}
                    </label>
                    <input
                      type="text"
                      value={item.description}
                      onChange={(e) => handleItemChange(item.id, 'description', e.target.value)}
                      placeholder="Description"
                      className="h-9 w-full rounded-lg border border-gray-300 bg-white px-3 text-sm text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                    />
                  </div>

                  <div className="space-y-1">
                    <label className="text-xs font-medium text-gray-700">Type</label>
                    <select
                      value={item.itemType}
                      onChange={(e) => handleItemChange(item.id, 'itemType', e.target.value)}
                      className="h-9 w-full rounded-lg border border-gray-300 bg-white px-3 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                    >
                      <option value="SERVICE">Service</option>
                      <option value="MATERIAL">Material</option>
                      <option value="LABOUR">Labour</option>
                      <option value="CALLOUT">Call-out</option>
                    </select>
                  </div>

                  <div className="space-y-1">
                    <label className="text-xs font-medium text-gray-700">Qty</label>
                    <input
                      type="number"
                      min="0"
                      value={item.quantity}
                      onChange={(e) => handleItemChange(item.id, 'quantity', e.target.value)}
                      className="h-9 w-full rounded-lg border border-gray-300 bg-white px-3 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                    />
                  </div>

                  <div className="space-y-1">
                    <label className="text-xs font-medium text-gray-700">Price</label>
                    <input
                      type="number"
                      min="0"
                      value={item.unitPrice}
                      onChange={(e) => handleItemChange(item.id, 'unitPrice', e.target.value)}
                      className="h-9 w-full rounded-lg border border-gray-300 bg-white px-3 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                    />
                  </div>

                  <button
                    type="button"
                    onClick={() => handleRemoveItem(item.id)}
                    className="mt-5 inline-flex h-8 w-8 items-center justify-center rounded-lg border border-gray-200 text-gray-400 hover:text-red-500 hover:border-red-200 transition-colors"
                  >
                    <TrashIcon className="h-4 w-4" />
                  </button>
                </div>
              ))}

              <button
                type="button"
                onClick={handleAddItem}
                className="inline-flex items-center gap-1 text-xs font-medium text-orange-600 hover:text-orange-700"
              >
                <PlusIcon className="h-4 w-4" />
                <span>Add item</span>
              </button>
            </div>

            <div className="grid grid-cols-1 gap-3 md:grid-cols-2 border-t border-gray-100 pt-4 mt-2">
              <div className="space-y-1">
                <label className="text-xs font-medium text-gray-700">Notes</label>
                <input
                  type="text"
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Optional notes"
                  className="h-9 w-full rounded-lg border border-gray-300 bg-white px-3 text-sm text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                />
              </div>
              <div className="space-y-1">
                <label className="text-xs font-medium text-gray-700">Terms</label>
                <input
                  type="text"
                  value={terms}
                  onChange={(e) => setTerms(e.target.value)}
                  placeholder="Optional terms"
                  className="h-9 w-full rounded-lg border border-gray-300 bg-white px-3 text-sm text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                />
              </div>
            </div>
          </div>
        </div>

        <div className="sticky top-4 self-start">
            <div className="mb-3 flex items-center justify-between">
              <p className="text-xs font-medium text-[#515959] uppercase tracking-[0.12em]">Preview</p>
              <span className="text-[10px] font-medium text-orange-600 uppercase tracking-[0.12em]">
                What your client will see
              </span>
            </div>

            {/* A4-ratio document */}
            <div
              className="mx-auto bg-white shadow-md border border-[#E6E6E4] rounded-sm overflow-hidden"
              style={{ aspectRatio: '210 / 297', maxWidth: '100%' }}
            >
              <div className="h-full w-full p-6 text-[9px] leading-snug text-[#181818] flex flex-col">
                {/* HEADER */}
                <div className="flex items-start justify-between gap-4">
                  <div className="flex items-start gap-3 min-w-0">
                    {companyLogoUrl ? (
                      <img
                        src={companyLogoUrl}
                        alt={company?.name || 'Company logo'}
                        className="h-10 w-10 rounded object-contain border border-[#E6E6E4] bg-white flex-shrink-0"
                      />
                    ) : (
                      <div className="h-10 w-10 rounded border border-dashed border-[#E6E6E4] bg-[#FAFAFA] flex items-center justify-center text-[8px] text-[#9AA0A0] flex-shrink-0">
                        LOGO
                      </div>
                    )}
                    <div className="min-w-0">
                      <div className="text-[13px] font-semibold text-[#181818] leading-tight truncate">
                        {company?.name || 'Your company'}
                      </div>
                      <div className="mt-0.5 text-[8px] text-[#616a68] truncate">
                        {[company?.phone, company?.businessEmail].filter(Boolean).join('  ·  ')}
                      </div>
                      {company?.address && (
                        <div className="text-[8px] text-[#616a68] truncate">{company.address}</div>
                      )}
                    </div>
                  </div>
                  <div className="text-right flex-shrink-0">
                    <div className="text-[9px] font-semibold text-orange-600 tracking-[0.08em]">QUOTATION</div>
                    <div className="text-[13px] font-semibold text-[#181818] leading-tight">#DRAFT</div>
                    <div className="mt-1 text-[8px] text-[#616a68]">
                      Issued  {new Date().toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}
                    </div>
                    <div className="text-[8px] text-[#616a68]">
                      Valid until  {validUntil ? new Date(validUntil).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '—'}
                    </div>
                  </div>
                </div>

                <div className="mt-3 border-t border-[#E6E6E4]" />

                {/* BILL TO / SITE */}
                <div className="mt-3 grid grid-cols-2 gap-4">
                  <div>
                    <div className="text-[8px] font-semibold uppercase tracking-[0.08em] text-[#8d9392]">Bill to</div>
                    <div className="mt-1 text-[10px] font-semibold text-[#181818] truncate">{clientName || 'Client name'}</div>
                    <div className="mt-0.5 text-[8px] text-[#616a68] whitespace-pre-wrap line-clamp-3">
                      {clientAddress || 'Client address'}
                    </div>
                  </div>
                  <div>
                    <div className="text-[8px] font-semibold uppercase tracking-[0.08em] text-[#8d9392]">Site</div>
                    <div className="mt-1 text-[10px] font-semibold text-[#181818] truncate">{jobTitle || 'Job title'}</div>
                    <div className="mt-0.5 text-[8px] text-[#616a68] truncate">
                      {[siteAddress, sitePostcode].filter(Boolean).join(', ') || 'Same as billing address'}
                    </div>
                  </div>
                </div>

                {description && (
                  <div className="mt-3">
                    <div className="text-[8px] font-semibold uppercase tracking-[0.08em] text-[#8d9392]">Description</div>
                    <div className="mt-0.5 text-[9px] text-[#181818] whitespace-pre-wrap line-clamp-3">{description}</div>
                  </div>
                )}

                {/* ITEMS TABLE */}
                <div className="mt-3">
                  <div className="grid grid-cols-[1fr_36px_64px_72px] items-center gap-2 bg-[#f8f7f5] px-2 py-1.5 text-[8px] font-semibold uppercase tracking-[0.06em] text-[#616a68]">
                    <span>Description</span>
                    <span className="text-center">Qty</span>
                    <span className="text-right">Unit price</span>
                    <span className="text-right">Amount</span>
                  </div>
                  <div>
                    {items.map((item) => {
                      const qty = parseFloat(item.quantity || '0') || 0;
                      const unit = parseFloat(item.unitPrice || '0') || 0;
                      const lineTotal = qty * unit;
                      return (
                        <div
                          key={item.id}
                          className="grid grid-cols-[1fr_36px_64px_72px] items-start gap-2 border-b border-[#E6E6E4] px-2 py-1.5 text-[9px] text-[#181818]"
                        >
                          <span className="leading-snug break-words line-clamp-3">{item.description || 'Item description'}</span>
                          <span className="text-center">{qty ? (Number.isInteger(qty) ? qty : qty.toFixed(2)) : ''}</span>
                          <span className="text-right">{lineTotal ? formatMoney(unit) : ''}</span>
                          <span className="text-right font-semibold">{lineTotal ? formatMoney(lineTotal) : ''}</span>
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* TOTALS */}
                <div className="mt-4 ml-auto w-56 space-y-1 text-[9px]">
                  <div className="flex items-center justify-between text-[#616a68]">
                    <span>Subtotal</span>
                    <span className="text-[#181818]">{formatMoney(subtotal)}</span>
                  </div>
                  <div className="flex items-center justify-between text-[#616a68]">
                    <span>VAT ({vatPercent}%)</span>
                    <span className="text-[#181818]">{formatMoney(vatAmount)}</span>
                  </div>
                  <div className="mt-1 flex items-center justify-between border-t border-[#E6E6E4] pt-1 text-[11px] font-semibold text-[#181818]">
                    <span>Total</span>
                    <span>{formatMoney(total)}</span>
                  </div>
                </div>

                {/* Notes / Terms / Footer */}
                <div className="mt-auto pt-3">
                  {notes && (
                    <div className="mb-2">
                      <div className="text-[8px] font-semibold uppercase tracking-[0.08em] text-[#8d9392]">Notes</div>
                      <div className="mt-0.5 text-[8px] text-[#181818] whitespace-pre-wrap line-clamp-2">{notes}</div>
                    </div>
                  )}
                  {terms && (
                    <div className="mb-2">
                      <div className="text-[8px] font-semibold uppercase tracking-[0.08em] text-[#8d9392]">Terms</div>
                      <div className="mt-0.5 text-[8px] text-[#616a68] whitespace-pre-wrap line-clamp-2">{terms}</div>
                    </div>
                  )}
                  <div className="border-t border-[#E6E6E4] mt-2 pt-1.5 flex items-center justify-between text-[7px] text-[#8d9392]">
                    <span className="truncate">
                      {[
                        company?.vatNumber ? `VAT ${company.vatNumber}` : null,
                        company?.registrationNumber ? `Reg ${company.registrationNumber}` : null,
                        company?.gasRegistrationNumber ? `Gas Safe ${company.gasRegistrationNumber}` : null,
                      ].filter(Boolean).join('  ·  ')}
                    </span>
                    <span>Thank you for your business.</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
      </div>
    </div>
  );
}

export default function Quotes() {
  const location = useLocation();
  const navigate = useNavigate();

  const [mode, setMode] = useState<'list' | 'create'>(() => {
    const params = new URLSearchParams(location.search);
    return params.get('mode') === 'create' ? 'create' : 'list';
  });

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const urlMode = params.get('mode');
    setMode(urlMode === 'create' ? 'create' : 'list');
  }, [location.search]);

  const beginCreate = () => {
    setMode('create');
    navigate({ pathname: location.pathname, search: '?mode=create' });
  };

  const cancelCreate = () => {
    setMode('list');
    navigate({ pathname: location.pathname, search: '' });
  };

  // Use React Query for cached data
  const { data: allQuotes = [], isLoading: loading, error } = useQuotesData();
  const [filteredQuotes, setFilteredQuotes] = useState<any[]>([]);

  // Pagination and filters state
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const itemsPerPage = 10;

  // Detail drawer state
  const [selectedQuote, setSelectedQuote] = useState<any | null>(null);

  // Client-side filtering - no loading, instant results with cached data
  useEffect(() => {
    if (!allQuotes || allQuotes.length === 0) {
      setFilteredQuotes([]);
      return;
    }

    console.log('[Quotes] Received allQuotes:', allQuotes);
    let filtered = [...allQuotes];

    // Apply search filter
    if (searchTerm) {
      filtered = filtered.filter((quote) =>
        quote.quoteNumber?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        quote.client?.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        quote.client?.company?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        quote.jobTitle?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Apply status filter
    if (statusFilter && statusFilter !== 'all') {
      filtered = filtered.filter((quote) =>
        quote.status?.toLowerCase() === statusFilter.toLowerCase()
      );
    }

    setFilteredQuotes(filtered);
    setCurrentPage(1); // Reset to first page when filters change
  }, [allQuotes, searchTerm, statusFilter]);

  // Show loading state
  if (loading) {
    return (
      <div className="page-container">
        <div className="page-header">
          <h1 className="page-title">Quotes</h1>
          <p className="page-subtitle">Loading...</p>
        </div>
        <div className="table-container">
          {[...Array(5)].map((_, i) => (
            <div key={i} className="loading-skeleton skeleton-row" />
          ))}
        </div>
      </div>
    );
  }

  // Show error state if there's an error
  if (error) {
    return (
      <div className="page-container">
        <div className="page-header">
          <div>
            <h1 className="page-title">Quotes</h1>
            <p className="page-subtitle">Create and manage quotes for potential jobs and track your sales pipeline.</p>
          </div>
          <div className="header-actions">
            <button
              className="btn btn-primary"
              onClick={beginCreate}
            >
              Create Quotes
            </button>
          </div>
        </div>
        <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
          <h3 className="text-lg font-medium text-red-900 mb-2">Error Loading Quotes</h3>
          <p className="text-red-700 mb-4">{error?.message || 'Failed to load quotes'}</p>
          <button
            onClick={() => window.location.reload()}
            className="btn btn-primary"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  // Calculate pagination for filtered results - ensure filteredQuotes is not empty
  const quotesToShow = filteredQuotes.length > 0 ? filteredQuotes : allQuotes;
  const totalPages = Math.ceil(quotesToShow.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedQuotes = quotesToShow.slice(startIndex, startIndex + itemsPerPage);

  if (allQuotes.length === 0 && !loading) {
    return (
      <div className="page-container">
        {mode === 'create' ? (
          <QuoteComposer onCancel={cancelCreate} />
        ) : (
          <>
            <div className="page-header">
              <h1 className="page-title">Quotes</h1>
            </div>
            <EmptyState
              icon={DocumentTextIcon}
              title="No quotes yet"
              description="Create quotes for potential jobs to track your sales pipeline."
              actionLabel="Create Quote"
              onAction={beginCreate}
            />
          </>
        )}
      </div>
    );
  }

  if (mode === 'create') {
    return (
      <div className="page-container">
        <QuoteComposer onCancel={cancelCreate} />
      </div>
    );
  }

  return (
    <div className="page-container">
      {/* Page Header */}
      <div className="mb-6 border-b border-[#E6E6E4] pb-3">
          <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
            <div>
              <h1 className="text-2xl font-semibold text-[#191919] leading-tight">Quotes</h1>
              <p className="mt-1 text-xs font-normal text-[#515959]">
                Create, send, and track job quotes before converting them into scheduled work.
              </p>
            </div>

            <div className="flex flex-1 items-center justify-start gap-3 md:justify-end md:gap-4">
              {/* Header Search */}
              <div className="relative w-full max-w-xs">
                <SearchIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[#686E6C]" />
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Search quotes..."
                  className="h-9 w-full rounded-lg border border-[#CDCDCD] bg-[#FEFEFE] pl-9 pr-3 text-sm text-[#383838] placeholder:text-[#686E6C] focus:outline-none focus:ring-2 focus:ring-[#CDCDCD]/80"
                />
              </div>

              {/* Primary Action */}
              <button
                type="button"
                onClick={beginCreate}
                className="inline-flex items-center justify-center rounded-lg border border-[#CDCDCD] bg-[#FEFEFE] px-3 py-2 text-sm font-medium text-[#383838] hover:bg-[#F3F2F0] transition-colors"
              >
                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                Create quote
              </button>
            </div>
          </div>
        </div>

      {/* Filters Bar */}
      <div className="filters-bar">
        <div className="filter-group">
          <select 
            className="filter-select"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <option value="">All Quotes</option>
            <option value="draft">Draft</option>
            <option value="sent">Sent</option>
            <option value="accepted">Accepted</option>
            <option value="rejected">Rejected</option>
            <option value="expired">Expired</option>
          </select>
          
          <select className="filter-select">
            <option>This Month</option>
            <option>Last Month</option>
            <option>Last 3 Months</option>
            <option>This Year</option>
          </select>
          
          <select className="filter-select">
            <option>All Clients</option>
            <option>John Smith</option>
            <option>Sarah Wilson</option>
            <option>Robert Taylor</option>
          </select>
        </div>
        
        <div className="flex items-center gap-3 ml-auto">
          <button className="btn btn-secondary">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5M16.5 12L12 16.5m0 0L7.5 12m4.5 4.5V3" />
            </svg>
          </button>
        </div>
      </div>

      {/* Table Container */}
      <div className="table-container">
        <div className="table-wrapper">
          <table className="transactions-table">
            <thead className="table-header">
              <tr>
                <th className="col-checkbox">
                  <input type="checkbox" className="table-checkbox" />
                </th>
                <th className="col-description">Quote Details</th>
                <th className="col-account">Client & Job</th>
                <th className="col-amount">Amount</th>
                <th className="col-date">Created</th>
                <th className="col-payment">Valid Until</th>
                <th className="col-actions">Status</th>
                <th className="col-actions"></th>
              </tr>
            </thead>
            <tbody>
              {paginatedQuotes.map((quote: any) => {
                const total = Number(quote?.totalAmount ?? 0);
                const status = String(quote?.status || 'DRAFT');
                return (
                  <tr
                    key={quote?.id ?? Math.random()}
                    className="table-row cursor-pointer"
                    onClick={(e) => {
                      // Ignore clicks on the action menu cell
                      const target = e.target as HTMLElement;
                      if (target.closest('.col-actions') || target.closest('button') || target.closest('input')) return;
                      setSelectedQuote(quote);
                    }}
                  >
                    <td className="col-checkbox">
                      <input type="checkbox" className="table-checkbox" />
                    </td>
                    <td className="col-description">
                      <div className="transaction-description">
                        <div className="transaction-icon-wrapper">
                          <DocumentTextIcon
                            className="w-5 h-5"
                            style={{ color: '#6B7280' }}
                          />
                        </div>
                        <div className="transaction-details">
                          <h4>{quote?.quoteNumber || '—'}</h4>
                          <p>{quote?.jobTitle || ''}</p>
                        </div>
                      </div>
                    </td>
                    <td className="col-account">
                      <div className="flex flex-col">
                        <div className="flex items-center mb-1">
                          <UserIcon className="w-4 h-4 text-gray-400 mr-2" />
                          <span className="text-sm font-medium">{quote?.client?.name || 'Unknown Client'}</span>
                        </div>
                        <div className="flex items-center">
                          <CalendarDaysIcon className="w-4 h-4 text-gray-400 mr-2" />
                          <span className="text-sm text-gray-600">{quote?.client?.company || 'No company'}</span>
                        </div>
                      </div>
                    </td>
                    <td className="col-amount">
                      <span className="text-sm font-semibold">£{(Number.isFinite(total) ? total : 0).toFixed(2)}</span>
                    </td>
                    <td className="col-date">
                      <span className="text-sm">{formatDate(quote?.createdAt)}</span>
                    </td>
                    <td className="col-payment">
                      <span className="text-sm">{formatDate(quote?.validUntil)}</span>
                    </td>
                    <td className="col-actions">
                      <span className={`status-badge ${status.toLowerCase()}`}>
                        {status}
                      </span>
                      {status === 'ACCEPTED' && (
                        <div className="flex items-center mt-1 text-xs text-green-600">
                          <ArrowRightIcon className="w-3 h-3 mr-1" />
                          Convert to Job
                        </div>
                      )}
                    </td>
                    <td className="col-actions">
                      {quote?.id && (
                        <QuoteActions
                          quote={quote}
                          onEdit={(q) => console.log('Edit quote:', q)}
                          onView={(q) => setSelectedQuote(q)}
                        />
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        
        {/* Pagination */}
        <div className="pagination">
          <div className="pagination-info">
            Page {currentPage} of {totalPages || 1}
          </div>
          <div className="pagination-controls">
            <button 
              className="page-btn"
              disabled={currentPage === 1}
              onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
            >
              <ChevronLeftIcon className="w-4 h-4" />
            </button>
            {[...Array(Math.min(5, totalPages))].map((_, i) => {
              const pageNum = i + 1;
              return (
                <button 
                  key={pageNum}
                  className={`page-btn ${currentPage === pageNum ? 'active' : ''}`}
                  onClick={() => setCurrentPage(pageNum)}
                >
                  {pageNum}
                </button>
              );
            })}
            <button 
              className="page-btn"
              disabled={currentPage === totalPages}
              onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
            >
              <ChevronRightIcon className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>

      {selectedQuote && (
        <QuoteDetailDrawer
          quote={selectedQuote}
          onClose={() => setSelectedQuote(null)}
        />
      )}
    </div>
  );
}
